import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Select, FormControl, InputLabel,
    Grid, Card, CardContent, Avatar, Divider, LinearProgress,
    Tooltip, Alert, Tab, Tabs, Collapse, CircularProgress,
    List, ListItem, ListItemIcon, ListItemText, Container, Stack
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    ArrowBack,
    Payment,
    Visibility,
    CheckCircle,
    Person,
    Refresh,
    Warning,
    BugReport,
    ExpandMore,
    ExpandLess,
    HelpOutline,
    Settings,
    People,
    AttachMoney,
    FileDownload as FileDownloadIcon,
    History,
    ReceiptLong,
    AccountBalanceWallet,
    CalendarMonth,
    Groups,
    Description
} from '@mui/icons-material';
import styled from 'styled-components';
import {
    getAllSalaryRecords,
    deleteSalaryRecord,
    getEmployeesWithSalaryStatus
} from '../../../redux/salaryRelated/salaryHandle';
import { getAllTeachers } from '../../../redux/teacherRelated/teacherHandle';
import { getAllSimpleStaffs } from '../../../redux/staffRelated/staffHandle';
import { underControl, clearEmployees } from '../../../redux/salaryRelated/salarySlice';
import { useToast } from '../../../context/ToastContext';
import { exportToExcel, getCurrentDateString } from '../../../utils/excelExport';
import axios from 'axios';
import { getNepaliMonthOptions, getNepaliYearOptions, getCurrentBSDate } from '../../../utils/customDate';

// Styled Components for Premium UI
const GlassCard = styled(Card)`
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(10px);
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05) !important;
    transition: all 0.3s ease;
    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1) !important;
    }
`;

const GradientHeader = styled(Box)`
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    border-radius: 0 0 40px 40px;
    padding: 60px 0;
    margin-bottom: -40px;
    color: white;
`;

const StatIcon = styled(Box)`
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.bg || 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.color || 'white'};
`;

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NP', {
        style: 'currency',
        currency: 'NPR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

const ShowSalary = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const { currentUser } = useSelector(state => state.user);
    const { salaryRecords, loading, error, employeesWithSalary } = useSelector(state => state.salary);
    const { teachersList } = useSelector(state => state.teacher);
    const { staffList } = useSelector(state => state.staff);

    const schoolId = currentUser?._id;
    const currentBS = getCurrentBSDate().split('-');
    const [selectedMonth, setSelectedMonth] = useState(currentBS[1]);
    const [selectedYear, setSelectedYear] = useState(currentBS[0]);
    const [tabValue, setTabValue] = useState(0);
    const [loadingEmployees, setLoadingEmployees] = useState(false);

    const months = getNepaliMonthOptions();
    const years = getNepaliYearOptions();

    useEffect(() => {
        if (schoolId) {
            dispatch(getAllTeachers(schoolId));
            dispatch(getAllSimpleStaffs(schoolId));
        }
    }, [schoolId, dispatch]);

    useEffect(() => {
        if (schoolId) {
            fetchEmployeeStatus();
        }
    }, [schoolId, selectedMonth, selectedYear, tabValue]);

    const fetchEmployeeStatus = async () => {
        setLoadingEmployees(true);
        const category = tabValue === 0 ? 'Teacher' : 'Staff';
        try {
            await dispatch(getEmployeesWithSalaryStatus(schoolId, category, selectedMonth, selectedYear));
        } catch (err) {
            console.error('Error fetching employee status:', err);
        } finally {
            setLoadingEmployees(false);
        }
    };

    const handleExport = () => {
        const data = (employeesWithSalary || []).map(emp => ({
            'Employee Name': emp.name,
            'Email': emp.email,
            'Category': tabValue === 0 ? 'Teacher' : 'Staff',
            'Position': emp.position,
            'Base Salary': emp.baseSalary,
            'Allowances': emp.totalAllowances,
            'Deductions': emp.totalDeductions,
            'Net Payout': emp.netSalary,
            'Payment Status': emp.isPaidForSelectedMonth ? 'Paid' : 'Pending',
            'Period': `${months.find(m => m.value === selectedMonth)?.label || selectedMonth} ${selectedYear}`
        }));

        const fileName = `Payroll_Report_${selectedMonth}_${selectedYear}_${getCurrentDateString()}`;
        exportToExcel(data, fileName, 'Payroll History');
    };

    const stats = {
        totalEmployees: (teachersList?.length || 0) + (staffList?.length || 0),
        paidCount: (employeesWithSalary || []).filter(e => e.isPaidForSelectedMonth).length,
        totalDisbursement: (employeesWithSalary || []).reduce((sum, e) => sum + (e.isPaidForSelectedMonth ? (e.netSalary || 0) : 0), 0),
        pendingDisbursement: (employeesWithSalary || []).reduce((sum, e) => sum + (!e.isPaidForSelectedMonth ? (e.netSalary || 0) : 0), 0)
    };

    return (
        <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <GradientHeader>
                <Container maxWidth="xl">
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 6, gap: 2 }}>
                        <IconButton 
                            onClick={() => navigate('/Admin/dashboard')} 
                            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box>
                            <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em' }}>
                                Payroll Analytics
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 400 }}>
                                High-level financial overview and audit trails
                            </Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/Admin/salary/add')}
                                sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, borderRadius: '14px', px: 4, py: 1.5, textTransform: 'none', fontWeight: 700 }}
                            >
                                Distribute Salaries
                            </Button>
                        </Stack>
                    </Box>

                    <Grid container spacing={4}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Institutional Staff" value={stats.totalEmployees} icon={<Groups />} color="#6366f1" bg="rgba(99, 102, 241, 0.2)" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Disbursed (Month)" value={formatCurrency(stats.totalDisbursement)} icon={<CheckCircle />} color="#10b981" bg="rgba(16, 185, 129, 0.2)" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Remaining Payouts" value={formatCurrency(stats.pendingDisbursement)} icon={<AccountBalanceWallet />} color="#f59e0b" bg="rgba(245, 158, 11, 0.2)" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Settlement Ratio" value={`${stats.paidCount}/${employeesWithSalary?.length || 0}`} icon={<History />} color="#8b5cf6" bg="rgba(139, 92, 246, 0.2)" />
                        </Grid>
                    </Grid>
                </Container>
            </GradientHeader>

            <Container maxWidth="xl" sx={{ mt: 10, pb: 10 }}>
                {/* Global Filters */}
                <GlassCard sx={{ mb: 6, p: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, p: 2, alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mr: 2 }}>Audit Filter</Typography>
                        
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', bgcolor: '#f1f5f9', p: 1, borderRadius: '16px' }}>
                            <CalendarMonth sx={{ color: '#64748b', ml: 1 }} />
                            <Select
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                size="small"
                                sx={{ border: 'none', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 700 }}
                            >
                                {months.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                            </Select>
                            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                            <Select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                                size="small"
                                sx={{ border: 'none', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 700 }}
                            >
                                {years.map(y => <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>)}
                            </Select>
                        </Box>

                        <Tabs
                            value={tabValue}
                            onChange={(e, v) => setTabValue(v)}
                            sx={{ 
                                bgcolor: '#f1f5f9', 
                                borderRadius: '16px', 
                                p: 0.5,
                                '& .MuiTabs-indicator': { display: 'none' },
                                '& .MuiTab-root': {
                                    borderRadius: '12px',
                                    minHeight: '40px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    px: 4,
                                    '&.Mui-selected': { bgcolor: '#0f172a', color: 'white' }
                                }
                            }}
                        >
                            <Tab label="Teachers" onClick={() => dispatch(clearEmployees())} />
                            <Tab label="Administrative Staff" onClick={() => dispatch(clearEmployees())} />
                        </Tabs>

                        <Box sx={{ flexGrow: 1 }} />
                        
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExport}
                            disabled={!employeesWithSalary || employeesWithSalary.length === 0}
                            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 3 }}
                        >
                            Export Audit Report
                        </Button>
                        
                        <Tooltip title="Refresh Registry">
                            <IconButton onClick={fetchEmployeeStatus} color="primary" sx={{ bgcolor: '#f1f5f9' }}>
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </GlassCard>

                {/* Audit Logs */}
                <Typography variant="h5" sx={{ fontWeight: 900, mb: 3, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ReceiptLong color="primary" /> Transactional Registry
                </Typography>

                {loadingEmployees ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
                        <CircularProgress size={48} thickness={4} />
                        <Typography variant="subtitle1" color="text.secondary">Fetching audit trails...</Typography>
                    </Box>
                ) : (!employeesWithSalary || employeesWithSalary.length === 0) ? (
                    <Paper sx={{ p: 10, textAlign: 'center', borderRadius: '32px', bgcolor: 'rgba(255,255,255,0.5)', border: '1px dashed #cbd5e1' }}>
                        <Description sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#64748b' }}>No transactions found for this period</Typography>
                        <Typography variant="body1" color="text.secondary">Try adjusting your filters or initiate a new disbursement</Typography>
                    </Paper>
                ) : (
                    <GlassCard>
                        <TableContainer>
                            <Table sx={{ minWidth: 800 }}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontWeight: 800, color: '#64748b', py: 3 }}>EMPLOYEE</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#64748b' }}>PERIOD</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#64748b' }}>GROSS PAY</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#64748b' }}>DEDUCTIONS</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#64748b' }}>NET PAYABLE</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 800, color: '#64748b' }}>SETTLEMENT STATUS</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 800, color: '#64748b' }}>ACTIONS</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {employeesWithSalary.map((emp) => (
                                        <TableRow key={emp._id} hover sx={{ '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.02)' } }}>
                                            <TableCell sx={{ py: 2.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar 
                                                        src={emp.photo ? `${process.env.REACT_APP_BASE_URL}/${emp.photo}` : ''}
                                                        sx={{ width: 44, height: 44, bgcolor: '#e2e8f0', color: '#64748b', fontWeight: 800 }}
                                                    >
                                                        {emp.name?.charAt(0)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>{emp.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{emp.position}</Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{months.find(m => m.value === selectedMonth)?.label || selectedMonth} {selectedYear}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency((emp.baseSalary || 0) + (emp.totalAllowances || 0))}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 600 }}>-{formatCurrency(emp.totalDeductions || 0)}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                                    {formatCurrency(emp.netSalary || 0)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip 
                                                    label={emp.isPaidForSelectedMonth ? "Settled" : "Pending"}
                                                    color={emp.isPaidForSelectedMonth ? "success" : "warning"}
                                                    sx={{ 
                                                        borderRadius: '10px', 
                                                        fontWeight: 800, 
                                                        fontSize: '0.75rem',
                                                        px: 1,
                                                        bgcolor: emp.isPaidForSelectedMonth ? '#dcfce7' : '#fef9c3',
                                                        color: emp.isPaidForSelectedMonth ? '#166534' : '#854d0e',
                                                        border: 'none'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                    <Tooltip title="View Receipt">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => navigate(`/Admin/salary/receipt/${emp._id}/${selectedMonth}/${selectedYear}`)}
                                                            sx={{ color: '#64748b', '&:hover': { color: '#3b82f6', bgcolor: 'rgba(59, 130, 246, 0.1)' } }}
                                                        >
                                                            <Visibility fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {!emp.isPaidForSelectedMonth && (
                                                        <Tooltip title="Initiate Payment">
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => navigate('/Admin/salary/add')}
                                                                sx={{ color: '#10b981', '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)' } }}
                                                            >
                                                                <Payment fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </GlassCard>
                )}
            </Container>
        </Box>
    );
};

// Sub-components
const StatCard = ({ title, value, icon, color, bg }) => (
    <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', p: 3, borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <StatIcon bg={bg} color={color}>{icon}</StatIcon>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{title}</Typography>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>{value}</Typography>
    </Box>
);

export default ShowSalary;
